import type { CategoryMeta, Movie, SortKey, SpecialTopic } from '@/types/movie';
import { movieItems } from '@/mocks/movies';
import { seriesItems } from '@/mocks/series';
import { specialItems } from '@/mocks/specials';

export const MOVIES = [...movieItems, ...seriesItems] as unknown as Movie[];

export const SPECIALS = specialItems as unknown as SpecialTopic[];

export const CATEGORIES: CategoryMeta[] = [
  {
    key: 'movie',
    title: '电影',
    enTitle: 'Movies',
    description: '浏览大雷影视电影分类，发现最新热门电影与高分佳作。',
    icon: 'ri-film-line',
  },
  {
    key: 'tv',
    title: '电视剧',
    enTitle: 'TV Series',
    description: '浏览大雷影视电视剧分类，追更最新热门剧集与口碑长剧。',
    icon: 'ri-tv-2-line',
  },
  {
    key: 'guochan',
    title: '国产剧',
    enTitle: 'Chinese Drama',
    description: '国产剧专区，收录内地、香港、台湾地区最新剧集与年代佳作。',
    icon: 'ri-building-4-line',
  },
  {
    key: 'oumei',
    title: '欧美剧',
    enTitle: 'Western Series',
    description: '欧美剧专区，涵盖英美法高分剧集、悬疑犯罪与科幻巨制。',
    icon: 'ri-global-line',
  },
  {
    key: 'hot',
    title: '热门',
    enTitle: 'Trending',
    description: '大雷影视热门榜单，聚合近期讨论度最高的电影与电视剧。',
    icon: 'ri-fire-line',
  },
  {
    key: 'latest',
    title: '最新',
    enTitle: 'Latest',
    description: '大雷影视最新上线，第一时间收录新上映电影与开播剧集。',
    icon: 'ri-time-line',
  },
];

const CHINESE_REGIONS = ['中国大陆', '中国香港', '中国台湾'];
const WESTERN_REGIONS = ['美国', '英国', '法国'];

export const REGION_OPTIONS = ['中国大陆', '中国香港', '中国台湾', '美国', '英国', '法国', '日本', '韩国', '印度'];
export const YEAR_OPTIONS = [2026, 2025, 2024, 2023, 2022, 2021];
export const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'default', label: '综合排序' },
  { key: 'rating', label: '评分最高' },
  { key: 'newest', label: '最新上线' },
];

export const getCategoryMeta = (key: string): CategoryMeta | undefined =>
  CATEGORIES.find((item) => item.key === key);

export const getCategoryMovies = (type: string): Movie[] => {
  switch (type) {
    case 'movie':
      return MOVIES.filter((item) => item.category === 'movie');
    case 'tv':
      return MOVIES.filter((item) => item.category === 'tv');
    case 'guochan':
      return MOVIES.filter((item) => CHINESE_REGIONS.includes(item.region));
    case 'oumei':
      return MOVIES.filter((item) => WESTERN_REGIONS.includes(item.region));
    case 'hot':
      return [...MOVIES].filter((item) => item.popularity >= 80).sort((a, b) => b.popularity - a.popularity);
    case 'latest':
      return [...MOVIES].filter((item) => item.year >= 2025).sort((a, b) => b.year - a.year || b.popularity - a.popularity);
    default:
      return [];
  }
};

export const filterCatalog = (
  list: Movie[],
  options: { region?: string; year?: string; sort?: SortKey },
): Movie[] => {
  const { region = 'all', year = 'all', sort = 'default' } = options;
  let next = list.filter((item) => {
    const matchRegion = region === 'all' || item.region === region;
    const matchYear = year === 'all' || String(item.year) === year;
    return matchRegion && matchYear;
  });

  if (sort === 'rating') {
    next = [...next].sort((a, b) => b.rating - a.rating);
  } else if (sort === 'newest') {
    next = [...next].sort((a, b) => b.year - a.year || b.popularity - a.popularity);
  } else {
    next = [...next].sort((a, b) => b.popularity - a.popularity);
  }

  return next;
};

export const searchCatalog = (keyword: string): Movie[] => {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];

  return MOVIES.filter((item) => {
    const haystack = [
      item.title,
      item.subtitle,
      item.director,
      item.region,
      item.genres.join(' '),
      item.cast.join(' '),
      item.summary,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  });
};

export const getMovieById = (id: string): Movie | undefined => MOVIES.find((item) => item.id === id);

export const getMoviesByIds = (ids: string[]): Movie[] =>
  ids.map((id) => MOVIES.find((item) => item.id === id)).filter((item): item is Movie => Boolean(item));

export const getRelatedMovies = (movie: Movie, limit = 6): Movie[] => {
  const scored = MOVIES.filter((item) => item.id !== movie.id).map((item) => {
    const sharedGenres = item.genres.filter((genre) => movie.genres.includes(genre)).length;
    const sameCategory = item.category === movie.category ? 2 : 0;
    const sameRegion = item.region === movie.region ? 1 : 0;
    return { item, score: sharedGenres * 3 + sameCategory + sameRegion };
  });

  return scored
    .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity)
    .slice(0, limit)
    .map((entry) => entry.item);
};

export const getFeaturedMovies = (): Movie[] => MOVIES.filter((item) => item.featured);

export const getLatestMovies = (limit = 12): Movie[] =>
  MOVIES.filter((item) => item.category === 'movie')
    .sort((a, b) => b.year - a.year || b.popularity - a.popularity)
    .slice(0, limit);

export const getPopularSeries = (limit = 12): Movie[] =>
  MOVIES.filter((item) => item.category === 'tv')
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);

export const getSpecialById = (id: string): SpecialTopic | undefined =>
  SPECIALS.find((item) => item.id === id);

export const REGIONS_FOR = (list: Movie[]): string[] =>
  REGION_OPTIONS.filter((region) => list.some((item) => item.region === region));

export const TOTAL_MOVIES = MOVIES.length;
export const TOTAL_FILMS = MOVIES.filter((item) => item.category === 'movie').length;
export const TOTAL_SERIES = MOVIES.filter((item) => item.category === 'tv').length;